import { ANIMATIONS, DEFAULT_ANIMATION, SHADES, DEFAULT_SHADE, FITS, DEFAULT_FIT } from "lib/slider";

const inputCls =
  "px-2.5 py-1.5 border-[1.5px] border-line rounded-lg font-sans text-[12.5px] outline-none focus:border-green bg-white";

/**
 * Per-slide options shared by every slider editor (Homepage slides,
 * Institutions → Hero slides). Edits the optional fields documented in
 * lib/slider.js: showText, textFromPrevious, animation, duration.
 *
 *   <SlideOptions slide={s} index={i} onChange={(patch) => ...}
 *                 onApplyAnimationToAll={(id) => ...} defaultDuration={5.5} />
 */
export default function SlideOptions({ slide, index, onChange, onApplyAnimationToAll, defaultDuration = 6 }) {
  const showText = slide.showText !== false;
  const samePrev = !!slide.textFromPrevious;
  const animation = slide.animation || DEFAULT_ANIMATION;
  const current = ANIMATIONS.find((a) => a.id === animation);
  const shade = slide.shade || DEFAULT_SHADE;
  const currentShade = SHADES.find((x) => x.id === shade);
  const fit = slide.fit || DEFAULT_FIT;
  const currentFit = FITS.find((x) => x.id === fit);

  return (
    <div className="mt-1 rounded-lg bg-[#f6f8f7] border border-line px-3 py-2.5 flex flex-wrap items-center gap-x-5 gap-y-2 text-[12.5px] text-ink">
      <label className="inline-flex items-center gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={showText}
          onChange={(e) => onChange({ showText: e.target.checked ? undefined : false })}
          className="cursor-pointer"
        />
        Show text on this slide
      </label>

      <label
        className={`inline-flex items-center gap-2 ${index === 0 ? "opacity-40 cursor-not-allowed" : "cursor-pointer"}`}
        title={index === 0 ? "The first slide has no previous slide to copy from" : "The photo changes but the caption stays the same"}
      >
        <input
          type="checkbox"
          checked={samePrev}
          disabled={index === 0}
          onChange={(e) => onChange({ textFromPrevious: e.target.checked || undefined })}
          className="cursor-pointer"
        />
        Same text as previous slide
      </label>

      <span className="inline-flex items-center gap-2">
        <span className="text-muted">Animation:</span>
        <select
          value={animation}
          onChange={(e) => onChange({ animation: e.target.value === DEFAULT_ANIMATION ? undefined : e.target.value })}
          className={inputCls}
          title={current?.hint}
        >
          {ANIMATIONS.map((a) => (
            <option key={a.id} value={a.id}>{a.label}</option>
          ))}
        </select>
        {onApplyAnimationToAll && (
          <button
            type="button"
            onClick={() => onApplyAnimationToAll(animation)}
            className="text-[12px] text-green font-semibold bg-transparent border-none cursor-pointer p-0 hover:underline"
            title="Use this animation for every slide in this slider"
          >
            Apply to all
          </button>
        )}
      </span>

      <span className="inline-flex items-center gap-2">
        <span className="text-muted">Photo fit:</span>
        <select
          value={fit}
          onChange={(e) => onChange({ fit: e.target.value === DEFAULT_FIT ? undefined : e.target.value })}
          className={inputCls}
          title={currentFit?.hint}
        >
          {FITS.map((x) => (
            <option key={x.id} value={x.id}>{x.label}</option>
          ))}
        </select>
      </span>

      <span className="inline-flex items-center gap-2">
        <span className="text-muted">Photo shade:</span>
        <select
          value={shade}
          onChange={(e) => onChange({ shade: e.target.value === DEFAULT_SHADE ? undefined : e.target.value })}
          className={inputCls}
          title={currentShade?.hint}
        >
          {SHADES.map((x) => (
            <option key={x.id} value={x.id}>{x.label}</option>
          ))}
        </select>
      </span>

      <label className="inline-flex items-center gap-2">
        <span className="text-muted">Show for</span>
        <input
          type="number"
          min="2"
          max="60"
          step="0.5"
          value={slide.duration ?? ""}
          placeholder={String(defaultDuration)}
          onChange={(e) => {
            const v = parseFloat(e.target.value);
            onChange({ duration: Number.isFinite(v) && v > 0 ? v : undefined });
          }}
          className={`${inputCls} w-[70px]`}
        />
        <span className="text-muted">sec</span>
      </label>
    </div>
  );
}
